import { useEffect, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { completeM365Connection } from "@/api/client/m365-connection"
import { Button } from "@/components/ui/button"

import {
  M365_RETURN_KEY,
  safeM365ReturnPath,
  takeM365AuthorizationResponse,
} from "../utils/m365-connection"

export function M365ConnectionCallbackPage() {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const client = useQueryClient()

  const started = useRef(false)

  const [failed, setFailed] = useState(false)

  const [returnPath] = useState(() =>
    safeM365ReturnPath(sessionStorage.getItem(M365_RETURN_KEY), location.origin)
  )

  useEffect(() => {
    if (started.current) return

    started.current = true

    const input = new URLSearchParams(takeM365AuthorizationResponse() ?? location.search)

    const response = new URLSearchParams()

    for (const key of ["code", "state", "error", "error_description"]) {
      const value = input.get(key)

      if (value) response.set(key, value)
    }

    // Remove the authorization code even when exchange fails; never persist it.

    history.replaceState(history.state, "", location.pathname)

    sessionStorage.removeItem(M365_RETURN_KEY)

    if (!response.has("state") || (!response.has("code") && !response.has("error"))) {
      setFailed(true)
      return
    }

    void completeM365Connection(response.toString())
      .then((data) => {
        client.setQueryData(["m365-connection"], data)

        void navigate(returnPath, { replace: true })
      })
      .catch(() => setFailed(true))
  }, [client, navigate, returnPath])

  return (
    <main className="mx-auto max-w-lg space-y-4 p-6">
      <h1 className="type-lead">
        {t("agents.connection.title", { defaultValue: "Microsoft 365 connection" })}
      </h1>

      <p role={failed ? "alert" : "status"}>
        {failed
          ? t("agents.connection.callback_failed", {
              defaultValue:
                "Connection was not completed. Return and reconnect with the same Microsoft account you use in this app.",
            })
          : t("agents.connection.connecting", { defaultValue: "Completing your connection…" })}
      </p>

      {failed ? (
        <Button onClick={() => navigate(returnPath, { replace: true })}>
          {t("agents.connection.return", { defaultValue: "Return" })}
        </Button>
      ) : null}
    </main>
  )
}
